import { Button } from "@/components/ui/button"
import { extractErrorMessage } from "@/lib/error"
import { hasRecentReload, isChunkLoadError, markReload } from "@/lib/lazy-with-retry"
import * as Sentry from "@sentry/react"
import { AlertTriangle, ArrowLeft, Home, RefreshCw } from "lucide-react"
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom"

export function RouteErrorBoundary() {
  const error = useRouteError()
  const navigate = useNavigate()

  const isChunkError = isChunkLoadError(error)

  // Auto-reload on chunk load error after deploy
  if (isChunkError && !hasRecentReload()) {
    markReload()
    window.location.reload()
    return null
  }

  // Extrair informações do erro
  let errorMessage = "Ocorreu um erro desconhecido"
  let errorStatus = ""

  if (isRouteErrorResponse(error)) {
    errorStatus = `${error.status}`
    errorMessage = error.statusText || error.data?.message || "Página não encontrada"
  } else if (error instanceof Error) {
    errorMessage = extractErrorMessage(error, "Ocorreu um erro inesperado")
  }

  if (!isChunkError) {
    Sentry.captureException(error)
  }

  // Determinar o título com base no status
  let title = "Ocorreu um erro"
  if (errorStatus === "404") {
    title = "Página não encontrada"
  } else if (errorStatus.startsWith("5")) {
    title = "Erro no servidor"
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <div className="bg-card border-border w-full max-w-xl space-y-8 rounded-lg border p-6 shadow-lg">
        <div className="flex flex-col items-center text-center">
          <div className="bg-muted mb-4 flex h-24 w-24 items-center justify-center rounded-full">
            <AlertTriangle className="h-12 w-12 text-amber-500" />
          </div>

          <h2 className="text-2xl font-bold">{title}</h2>
          {errorStatus && (
            <span className="text-muted-foreground text-lg font-semibold">{errorStatus}</span>
          )}

          <p className="text-muted-foreground mt-2">{errorMessage}</p>

          {process.env.NODE_ENV === "development" && error instanceof Error && (
            <div className="bg-muted mt-6 w-full overflow-auto rounded-md p-4 text-left">
              <p className="text-destructive font-mono text-sm">{error.message}</p>
              <div className="text-muted-foreground mt-3 overflow-auto font-mono text-xs">
                <pre className="max-h-60 overflow-y-auto p-2 whitespace-pre-wrap">
                  {error.stack?.split("\n").slice(0, 10).join("\n")}
                </pre>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col justify-center gap-4 sm:flex-row sm:gap-2">
          <Button onClick={() => navigate(-1)} variant="outline" className="flex-1">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>

          <Button onClick={() => navigate("/", { replace: true })} className="flex-1">
            <Home className="mr-2 h-4 w-4" />
            Página inicial
          </Button>

          {!errorStatus.match(/404|401|403/) && (
            <Button onClick={() => window.location.reload()} variant="secondary" className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              Recarregar
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
